import React, { useState } from 'react';
import { View } from 'react-native';
import Author from 'src/components/10-atoms/author/Author';
import HistoryItemCard from 'src/components/10-atoms/Card/HistoryItemCard';
import ErrorBoundary from 'src/components/10-atoms/ErrorBoundary';
import FormattedDate from 'src/components/10-atoms/date/FormattedDate';
import MoreOptionsButton from 'src/components/10-atoms/dropdown-menu/MoreOptionsButton';
import HTMLDiffRenderer from 'src/components/10-atoms/form/inputs/editor/html/HTMLEditor/DiffRenderer';
import Column from 'src/components/10-atoms/layout/Column';
import Row from 'src/components/10-atoms/layout/Row';
import Typography from 'src/components/10-atoms/typography/Typography';
import { TypographyVariant } from 'src/customizations/themes/10-parts/typography';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useUserStorage from 'src/modules/authentication/storage/user/hook';
import Comments from 'src/modules/review-pipeline/screens/ReviewDetails/components/Comments';
import useI18n from 'src/modules/translations/hooks/useI18n';
import BlockedContentMessage from 'src/modules/ugc-policy/components/BlockedContentMessage';
import { ICaseEntityType, ICaseType } from 'src/modules/ugc-policy/graphql/types';
import useUGCMenuOptions from 'src/modules/ugc-policy/hooks/useUGCMenuOptions';
import useUGCPolicy from 'src/modules/ugc-policy/hooks/useUGCPolicy';
function plainTextFromHtml(html) {
    return (html ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
const AnswerHistoryItem = ({ answer, contributor, historyIndex, onUGCPolicyUpdated, ...columnProps }) => {
    const [{ theme }] = useTheme(stylesGenerator);
    const { translate } = useI18n();
    const [diffFailed, setDiffFailed] = useState(false);
    const ugcMenuOptions = useUGCMenuOptions();
    const [userDetails] = useUserStorage();
    const answerHistory = answer.data.answerHistory ?? [];
    const data = answerHistory[historyIndex];
    const isAuthor = data.author.id === userDetails?.id;
    const currentAnswer = data.answer;
    const prevAnswer = answerHistory[historyIndex - 1]?.answer;
    const [ugcPolicyState, ugcPolicyActions] = useUGCPolicy({
        entity: {
            id: `${answer.id}:${historyIndex}`,
            type: ICaseEntityType.EP_WRITTEN_ANSWER,
        },
        reportAbuseType: ICaseType.EP_ABSUIVE_CONTENT,
        entityAuthor: data.author.id,
        contentBlockList: data.blockedBy,
        onBlockContentSuccess: onUGCPolicyUpdated,
        onUnblockContentSuccess: onUGCPolicyUpdated,
    });
    const isBlocked = ugcPolicyState.isContentBlocked || ugcPolicyState.isUserBlocked;
    const diffFallback = (React.createElement(Column, { pb: theme.spacing.standard.md, pt: theme.spacing.standard.xs },
        React.createElement(Typography, { color: theme.palette.typography.secondary }, plainTextFromHtml(currentAnswer) ||
            translate('Unable to display this revision'))));
    return isBlocked ? (React.createElement(BlockedContentMessage, { ph: theme.spacing.page, message: ugcPolicyState.isContentBlocked
            ? translate('You have blocked this Content')
            : translate('You have blocked the Author of this Content'), unblockingInProgress: ugcPolicyState.unblockContent.loading ||
            ugcPolicyState.unblockUser.loading, onUnblockPress: ugcPolicyState.isContentBlocked
            ? ugcPolicyActions.unblockContent
            : ugcPolicyActions.unblockUser })) : (React.createElement(HistoryItemCard, { ...columnProps },
        React.createElement(ErrorBoundary, { renderError: () => diffFallback }, diffFailed ? (diffFallback) : (React.createElement(HTMLDiffRenderer, { enableCachedImage: true, oldHtml: prevAnswer, newHtml: currentAnswer, pb: theme.spacing.standard.md, pt: theme.spacing.standard.xs, onError: () => setDiffFailed(true) }))),
        React.createElement(Row, { xAxis: "space-between", yAxis: "center", pv: theme.spacing.standard.sm },
            contributor ? React.createElement(Author, { flex: true, author: contributor }) : React.createElement(View, null),
            React.createElement(Row, { yAxis: "center", spacing: theme.spacing.standard.sm },
                React.createElement(FormattedDate, { mode: "relative", date: data.ctime, variant: TypographyVariant.CAPTION, color: theme.palette.typography.secondary }),
                !isAuthor && (React.createElement(MoreOptionsButton, { options: [
                        ugcMenuOptions.REPORT_ABUSE(ugcPolicyActions.reportAbuse),
                        ugcMenuOptions.BLOCK_AUTHOR(ugcPolicyActions.blockUser),
                        ugcMenuOptions.BLOCK_CONTENT(ugcPolicyActions.blockContent),
                    ] })))),
        !!data.comments && (React.createElement(Comments, { mb: theme.spacing.standard.sm, comments: data.comments }))));
};
const stylesGenerator = createThemedStyle(({}) => ({}));
export default AnswerHistoryItem;
